import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { X, FileDown, Trash2, Eye, Circle, ChevronDown, ChevronUp, Edit2 } from "lucide-react";
import { IrisZone } from "./IrisInteractiveMap";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

export interface SelectedZoneEntry {
  zone: IrisZone;
  eyeSide: "D" | "G";
  observation?: string;
  severityLevel?: string;
}

interface SelectedZonesPanelProps {
  selectedZones: SelectedZoneEntry[];
  onRemoveZone: (zoneId: string, eyeSide: "D" | "G") => void;
  onClearAll: () => void;
  onExportPdf: () => void;
  onUpdateZone?: (zoneId: string, eyeSide: "D" | "G", updates: Partial<SelectedZoneEntry>) => void;
  isExporting?: boolean;
}

export const SelectedZonesPanel: React.FC<SelectedZonesPanelProps> = ({
  selectedZones,
  onRemoveZone,
  onClearAll,
  onExportPdf,
  onUpdateZone,
  isExporting = false,
}) => {
  const [expandedZones, setExpandedZones] = React.useState<Set<string>>(new Set());

  if (selectedZones.length === 0) return null;

  const rightEyeZones = selectedZones.filter((z) => z.eyeSide === "D");
  const leftEyeZones = selectedZones.filter((z) => z.eyeSide === "G");

  const toggleExpanded = (key: string) => {
    setExpandedZones((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const severityOptions = [
    { value: "normal", label: "Normal", color: "bg-green-500" },
    { value: "attention", label: "À surveiller", color: "bg-amber-500" },
    { value: "priority", label: "Prioritaire", color: "bg-red-500" },
  ];

  const ZoneItem = ({ entry, eyeSide }: { entry: SelectedZoneEntry; eyeSide: "D" | "G" }) => {
    const key = `${eyeSide}-${entry.zone.id}`;
    const isExpanded = expandedZones.has(key);

    return (
      <Collapsible open={isExpanded} onOpenChange={() => toggleExpanded(key)}>
        <div className="rounded-lg border bg-card overflow-hidden">
          <CollapsibleTrigger asChild>
            <div className="flex items-center gap-2 p-2 cursor-pointer hover:bg-muted/50 transition-colors">
              <div
                className="w-3 h-3 rounded-full border shrink-0"
                style={{ backgroundColor: entry.zone.color, borderColor: entry.zone.color }}
              />
              <span className="text-sm flex-1 truncate font-medium">{entry.zone.label}</span>
              <Badge variant="outline" className="text-xs shrink-0">
                {entry.zone.ring === "inner" ? "Int." : "Ext."}
              </Badge>
              {entry.severityLevel && (
                <Badge
                  variant={entry.severityLevel === "priority" ? "destructive" : entry.severityLevel === "attention" ? "secondary" : "outline"}
                  className="text-xs shrink-0"
                >
                  {severityOptions.find((o) => o.value === entry.severityLevel)?.label}
                </Badge>
              )}
              {isExpanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
            </div>
          </CollapsibleTrigger>
          
          <CollapsibleContent>
            <div className="px-3 pb-3 pt-1 space-y-3 border-t bg-muted/20">
              {/* Zone info summary */}
              <div className="text-xs text-muted-foreground">
                <p className="line-clamp-2">{entry.zone.description_iridologie}</p>
              </div>

              {/* Priority selector */}
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Niveau de priorité</label>
                <Select
                  value={entry.severityLevel || "normal"}
                  onValueChange={(value) => onUpdateZone?.(entry.zone.id, eyeSide, { severityLevel: value })}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {severityOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value} className="text-xs">
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${opt.color}`} />
                          {opt.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Personal observation */}
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                  <Edit2 className="w-3 h-3" />
                  Mes observations
                </label>
                <Textarea
                  value={entry.observation || ""}
                  onChange={(e) => onUpdateZone?.(entry.zone.id, eyeSide, { observation: e.target.value })}
                  placeholder="Notez vos observations personnelles pour cette zone..."
                  className="text-xs min-h-[60px] resize-none"
                />
              </div>

              {/* Remove button */}
              <Button
                variant="ghost"
                size="sm"
                className="w-full text-xs text-muted-foreground hover:text-destructive"
                onClick={() => onRemoveZone(entry.zone.id, eyeSide)}
              >
                <X className="w-3 h-3 mr-1" />
                Retirer cette zone
              </Button>
            </div>
          </CollapsibleContent>
        </div>
      </Collapsible>
    );
  };

  const ZoneList = ({
    zones,
    title,
    eyeSide,
  }: {
    zones: SelectedZoneEntry[];
    title: string;
    eyeSide: "D" | "G";
  }) => {
    if (zones.length === 0) return null;

    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <Eye className="w-4 h-4" />
          {title} ({zones.length})
        </div>
        <div className="space-y-2">
          {zones.map((entry) => (
            <ZoneItem key={`${entry.eyeSide}-${entry.zone.id}`} entry={entry} eyeSide={eyeSide} />
          ))}
        </div>
      </div>
    );
  };

  return (
    <Card className="border-primary/30 bg-primary/5">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Circle className="w-4 h-4 text-primary" />
            Zones sélectionnées pour l'export ({selectedZones.length})
          </CardTitle>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearAll}
              className="text-xs text-muted-foreground hover:text-destructive"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1" />
              Tout effacer
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <ScrollArea className="max-h-[350px]">
          <div className="space-y-4 pr-2">
            <ZoneList zones={rightEyeZones} title="Œil Droit" eyeSide="D" />
            <ZoneList zones={leftEyeZones} title="Œil Gauche" eyeSide="G" />
          </div>
        </ScrollArea>

        <div className="pt-2 border-t">
          <p className="text-xs text-muted-foreground mb-3">
            Cliquez sur chaque zone pour ajouter vos observations et définir le niveau de priorité avant l'export.
          </p>
          <Button
            onClick={onExportPdf}
            disabled={isExporting}
            className="w-full gap-2"
          >
            <FileDown className="w-4 h-4" />
            {isExporting ? "Export en cours..." : "Exporter le PDF avec ces zones"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
